import { useEffect, useState } from "react";
import { BadgeCheck, Building2, ImagePlus, Save } from "lucide-react";
import { toast } from "sonner";
import { api } from "../lib/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import MediaPicker from "../components/MediaPicker";

const FIELDS = [
    ["contact_person", "Yetkili Adı Soyadı"],
    ["phone", "Telefon"],
    ["email", "E-posta"],
    ["tax_office", "Vergi Dairesi"],
    ["tax_no", "Vergi Numarası"],
    ["address", "Adres"],
    ["district", "Mahalle"],
    ["city", "Şehir"],
];

export default function Firma() {
    const [data, setData] = useState(null);
    const [pickerOpen, setPickerOpen] = useState(false);
    const [busy, setBusy] = useState(false);

    const load = () => api.get("/supplier/profile").then((r) => setData(r.data)).catch(() => {});
    useEffect(() => { load(); }, []);

    const save = async () => {
        setBusy(true);
        try {
            const payload = Object.fromEntries(FIELDS.map(([k]) => [k, data[k] || null]));
            payload.company_type = data.company_type || "corporate";
            payload.title = data.title || null;
            payload.contact_person = data.contact_person || null;
            payload.description = data.description || null;
            payload.logo = data.logo || null;
            await api.patch("/supplier/profile", payload);
            toast.success("Firma bilgileri kaydedildi");
        } catch (e) {
            toast.error(e.response?.data?.detail || "Kaydedilemedi");
        } finally {
            setBusy(false);
        }
    };

    if (!data) return <div className="h-96 animate-pulse rounded-2xl bg-muted" />;

    const isCorporate = (data.company_type || "corporate") === "corporate";

    return (
        <div className="mx-auto max-w-2xl space-y-6" data-testid="firma-page">
            <div>
                <h1 className="font-display text-3xl font-bold tracking-tight">Firma Bilgilerim</h1>
                <p className="mt-1 text-sm text-muted-foreground">Mağazalara gösterilen resmi bilgileriniz ve vitrin logonuz.</p>
            </div>

            <div className="rounded-2xl border bg-card p-6 shadow-sm">
                <div className="flex items-center gap-4">
                    {data.logo ? (
                        <img src={data.logo} alt="Logo" className="h-20 w-20 rounded-2xl border object-cover" data-testid="firma-logo-preview" />
                    ) : (
                        <div className="flex h-20 w-20 items-center justify-center rounded-2xl border border-dashed bg-muted"><Building2 className="h-8 w-8 text-muted-foreground" /></div>
                    )}
                    <div>
                        <Button size="sm" variant="outline" onClick={() => setPickerOpen(true)} data-testid="firma-logo-pick">
                            <ImagePlus className="mr-1.5 h-3.5 w-3.5" /> Ortam Kütüphanesi'nden Logo Seç
                        </Button>
                        <p className="mt-1.5 text-xs text-muted-foreground">Logo, tedarikçi vitrin sayfanızda görünür.</p>
                    </div>
                </div>
            </div>

            <div className="space-y-4 rounded-2xl border bg-card p-6 shadow-sm">
                <div className="flex gap-2">
                    {[["corporate", "Şirket"], ["individual", "Şahıs"]].map(([k, l]) => (
                        <button key={k} onClick={() => setData({ ...data, company_type: k })} className={`rounded-full border px-4 py-1.5 text-sm font-medium ${isCorporate === (k === "corporate") ? "border-primary bg-primary text-primary-foreground" : ""}`} data-testid={`firma-type-${k}`}>{l}</button>
                    ))}
                </div>
                <div>
                    <p className="mb-1 text-xs font-semibold text-muted-foreground">{isCorporate ? "Firma Ünvanı" : "Adı Soyadı"}</p>
                    <Input value={data.title || data.name || ""} onChange={(e) => setData({ ...data, title: e.target.value })} data-testid="firma-title" />
                </div>
                {FIELDS.map(([k, label]) => (
                    <div key={k}>
                        <p className="mb-1 text-xs font-semibold text-muted-foreground">{label}</p>
                        <Input value={data[k] || ""} onChange={(e) => setData({ ...data, [k]: e.target.value })} data-testid={`firma-${k}`} />
                    </div>
                ))}
                <div>
                    <p className="mb-1 text-xs font-semibold text-muted-foreground">Tanıtım Yazısı</p>
                    <Input value={data.description || ""} onChange={(e) => setData({ ...data, description: e.target.value })} data-testid="firma-description" />
                </div>
                <Button onClick={save} disabled={busy} className="bg-terra-600 text-white hover:bg-terra-700" data-testid="firma-save">
                    <Save className="mr-2 h-4 w-4" /> {busy ? "Kaydediliyor..." : "Kaydet"}
                </Button>
            </div>

            {data.verified && (
                <p className="flex items-center gap-2 text-sm text-emerald-700"><BadgeCheck className="h-4 w-4" /> Firmanız platform tarafından doğrulanmıştır.</p>
            )}

            <MediaPicker open={pickerOpen} onClose={() => setPickerOpen(false)} onSelect={(url) => { setData({ ...data, logo: url }); setPickerOpen(false); }} />
        </div>
    );
}
